import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../integrations/supabase/client";
import { exportToCSV } from "../utils/export";
import { Button } from "../components/Button";
import { Download, RefreshCw } from "lucide-react";

export default function Invoices() {
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const {
    data: invoices,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["invoices", statusFilter],
    queryFn: async () => {
      let query = supabase
        .from("asaas_payments")
        .select("*, user_id")
        .order("created_at", { ascending: false });

      if (statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Buscar emails dos usuários via admin-list-users Edge Function
      let userEmailMap = new Map<string, string>();
      try {
        const { data: usersData, error: usersError } =
          await supabase.functions.invoke("admin-list-users");
        if (!usersError && usersData?.users) {
          userEmailMap = new Map(
 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
            usersData.users.map((u: any) => [u.id, u.email || "N/A"]),
          );
        }
      } catch (err) {
        console.error("Erro ao buscar emails:", err);
        // Fallback: buscar via asaas_customers
        const userIds = [...new Set(data?.map((p) => p.user_id) || [])];
        if (userIds.length > 0) {
          const { data: customers } = await supabase
            .from("asaas_customers")
            .select("user_id, email")
            .in("user_id", userIds);

          if (customers) {
            userEmailMap = new Map(customers.map((c) => [c.user_id, c.email]));
          }
        }
      }

      return (
        data?.map((payment) => ({
          ...payment,
          user_email: userEmailMap.get(payment.user_id) || "N/A",
        })) || []
      );
    },
  });

  const handleExport = () => {
    if (!invoices) return;
    exportToCSV(invoices, "faturas", [
      { key: "user_email", label: "Cliente" },
      { key: "value", label: "Valor" },
      { key: "status", label: "Status" },
      { key: "due_date", label: "Vencimento" },
      { key: "payment_date", label: "Pagamento" },
    ]);
  };

  const statusLabels: Record<string, string> = {
    pending: "Pendente",
    confirmed: "Confirmado",
    received: "Recebido",
    overdue: "Vencido",
    refunded: "Reembolsado",
  };

  const statusColorsDark: Record<string, string> = {
    pending: "bg-yellow-500/20 text-yellow-400",
    confirmed: "bg-blue-500/20 text-blue-400",
    received: "bg-emerald-500/20 text-emerald-400",
    overdue: "bg-red-500/20 text-red-400",
    refunded: "bg-slate-500/20 text-slate-400",
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Faturas</h1>
          <p className="text-slate-400">Gestão de faturas e pagamentos ASAAS</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => refetch()} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button onClick={handleExport} variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Exportar CSV
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex gap-2 flex-wrap">
        {["all", "pending", "confirmed", "received", "overdue", "refunded"].map(
          (status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-lg transition-all ${
                statusFilter === status
                  ? "bg-red-500 text-white"
                  : "bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10"
              }`}
            >
              {status === "all" ? "Todas" : statusLabels[status]}
            </button>
          ),
        )}
      </div>

      {/* Cards com métricas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-500/10 border border-emerald-500/20 rounded-2xl p-6">
          <div className="text-sm text-slate-400 mb-2">Total Recebido</div>
          <div className="text-3xl font-bold text-emerald-400">
            R${" "}
            {(
              invoices
                ?.filter((i) => i.status === "received")
                .reduce((sum, i) => sum + i.value, 0) || 0
            ).toFixed(2)}
          </div>
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <div className="text-sm text-slate-400 mb-2">Pendente</div>
          <div className="text-3xl font-bold text-yellow-400">
            R${" "}
            {(
              invoices
                ?.filter((i) => i.status === "pending")
                .reduce((sum, i) => sum + i.value, 0) || 0
            ).toFixed(2)}
          </div>
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <div className="text-sm text-slate-400 mb-2">Vencido</div>
          <div className="text-3xl font-bold text-red-400">
            R${" "}
            {(
              invoices
                ?.filter((i) => i.status === "overdue")
                .reduce((sum, i) => sum + i.value, 0) || 0
            ).toFixed(2)}
          </div>
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <div className="text-sm text-slate-400 mb-2">Total Faturas</div>
          <div className="text-3xl font-bold text-white">
            {invoices?.length || 0}
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left py-4 px-6 text-xs font-medium text-slate-400 uppercase">
                Cliente
              </th>
              <th className="text-left py-4 px-6 text-xs font-medium text-slate-400 uppercase">
                Valor
              </th>
              <th className="text-left py-4 px-6 text-xs font-medium text-slate-400 uppercase">
                Status
              </th>
              <th className="text-left py-4 px-6 text-xs font-medium text-slate-400 uppercase">
                Vencimento
              </th>
              <th className="text-left py-4 px-6 text-xs font-medium text-slate-400 uppercase">
                Pagamento
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {isLoading ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-6 py-4 text-center text-slate-400"
                >
                  Carregando...
                </td>
              </tr>
            ) : invoices?.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-6 py-4 text-center text-slate-400"
                >
                  Nenhuma fatura encontrada
                </td>
              </tr>
            ) : (
              invoices?.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-white/5">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                    {invoice.user_email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                    R$ {invoice.value.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        statusColorsDark[invoice.status] ||
                        "bg-slate-500/20 text-slate-400"
                      }`}
                    >
                      {statusLabels[invoice.status]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                    {new Date(invoice.due_date).toLocaleDateString("pt-BR")}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                    {invoice.payment_date
                      ? new Date(invoice.payment_date).toLocaleDateString(
                          "pt-BR",
                        )
                      : "-"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
